import type { NuxtError } from '#app'
import type { OrderApiPayload } from '@api/orders/order/index.hook'
import type { ScopeMode } from '@utils/stores/scoped-entry'

import { useCheckoutStore } from '@stores/checkout'
import { useDisciplineStore } from '@stores/discipline'

import { useOrderApi } from '@api/orders/order/index.hook'

import { entryMode } from '@utils/stores/scoped-entry'

interface CheckoutOrderApiPayload extends OrderApiPayload {
  mode?: ScopeMode
}

interface CheckoutOrderApiOptions {
  onStart?: () => void
  onSuccess?: () => void
  onError?: () => void
  onFinish?: () => void
}

export const useCheckoutOrderApi = async (payload: CheckoutOrderApiPayload, options?: CheckoutOrderApiOptions): Promise<void | NuxtError> => {
  const checkoutStore = useCheckoutStore()
  const disciplineStore = useDisciplineStore()

  const { orderId, mode, ...orderPayload } = payload
  const isBackground = !mode
  const orderMode = mode ?? entryMode(checkoutStore.orderById, orderId, isBackground)
  const upsellOrderMode = mode ?? entryMode(checkoutStore.upsellOrderById, orderId, isBackground)

  checkoutStore.setActiveOrderId(orderId)
  const requestId = checkoutStore.beginOrder(orderId, orderMode)
  const upsellRequestId = checkoutStore.beginUpsellOrder(orderId, upsellOrderMode)

  return useOrderApi({ orderId, ...orderPayload }, {
    onStart: options?.onStart,
    onSuccess: (order) => {
      const discipline = order.disciplines?.[0]

      if (discipline?.kid?.id && discipline.code) {
        disciplineStore.setScope({ kidId: discipline.kid.id, code: discipline.code })
      }

      checkoutStore.settleOrder(orderId, requestId, order)
      checkoutStore.settleUpsellOrder(orderId, upsellRequestId, order)

      options?.onSuccess?.()
    },
    onError: () => {
      checkoutStore.failOrder(orderId, requestId)
      checkoutStore.failUpsellOrder(orderId, upsellRequestId)

      options?.onError?.()
    },
    onFinish: options?.onFinish,
  })
}
